import type { z } from 'zod';
import { safeParseJson } from '@shared/parse-json';
import { logger } from '../logger';
import {
  TRELLUS_EVENTS,
  TRELLUS_INBOUND_SCHEMAS,
  TRELLUS_MAX_DETAIL_CHARS,
  TRELLUS_MAX_ID_LIST_DETAIL_CHARS,
  TRELLUS_MAX_SELECTED_IDS,
  findObservedScopedSource,
  sourceMatchesScope,
  type TrellusBulkDialResult,
  type TrellusCapabilities,
  type TrellusCompletionPolicyChanged,
  type TrellusContext,
  type TrellusDialScope,
  type TrellusFocusResult,
  type TrellusGongConnectionState,
  type TrellusScopedSource,
  type TrellusSelectionKind,
  type TrellusSelectionResult,
  type TrellusSidebarStateEvent,
  type TrellusTaskEvent,
  type TrellusUiConfigurationChanged,
} from './host-schemas';

/**
 * Typed adapter for the Trellus (Chrome extension) host protocol — ported in
 * idea from the vendor's portable reference client, rewritten in repo style.
 *
 * One client = one context/sequence owner per document. It is a pure module:
 * it talks to a `target` (the `document`) with `CustomEvent`s, owns every
 * listener and timer it creates, and gives them all back in `dispose()`.
 *
 * Hard rules (the protocol guide's, restated because they are load-bearing):
 *  - Page events are untrusted UI hints. Nothing here authenticates anything.
 *  - Every wait is bounded (`timeoutMs`, default 3 s). An absent extension is
 *    a normal state: every method resolves to `{ ok: false }`, never hangs,
 *    never throws, and Clay stays fully usable.
 *  - A fresh context handshake precedes every selection broadcast.
 *  - The client NEVER dispatches in response to an inbound event, so a Trellus
 *    acknowledgement can never be echoed back as a new user action.
 *  - Inbound events are `safeParse`d; malformed / unknown ones are ignored and
 *    logged at debug.
 */

const log = logger.child({ category: 'trellus' });

export const TRELLUS_DEFAULT_TIMEOUT_MS = 3000;
/** Largest command envelope the extension accepts. */
const MAX_COMMAND_BYTES = 16_384;
/** Command `expires_at_ms` horizon; the extension also caps it at 5 s from receipt. */
const COMMAND_EXPIRY_MS = 5000;
const RECEIVED_EVENT_ID_CAP = 1000;

export type TrellusFailureReason =
  /** No reply within the bounded wait (extension absent, busy, or reloading). */
  | 'timeout'
  /** Extension answered `available:false`. */
  | 'unavailable'
  /** Extension answered with a terminal rejection (`reason_code` says why). */
  | 'rejected'
  | 'disposed'
  /** The advertised capabilities do not include the command. */
  | 'unsupported'
  | 'invalid_request'
  | 'dispatch_failed'
  /** A newer broadcast / context change made this one obsolete; nothing was applied. */
  | 'superseded'
  | 'context_changed'
  /** Context says no supported native table is showing (or not the caller's kind). */
  | 'view_unavailable'
  | 'kind_mismatch';

export type TrellusResult<T> =
  | { ok: true; value: T }
  | { ok: false; reason: TrellusFailureReason; reason_code?: string | null; retryable?: boolean };

export type TrellusClientEvent =
  | { type: 'capabilities'; capabilities: TrellusCapabilities | null }
  | { type: 'context'; context: TrellusContext }
  | { type: 'selection_result'; result: TrellusSelectionResult }
  | { type: 'focus_result'; result: TrellusFocusResult }
  | { type: 'sidebar_state'; state: TrellusSidebarStateEvent }
  | { type: 'bulk_dial_result'; result: TrellusBulkDialResult }
  | { type: 'gong_connection_state'; state: TrellusGongConnectionState }
  | { type: 'ui_configuration_changed'; change: TrellusUiConfigurationChanged }
  | { type: 'completion_policy_changed'; change: TrellusCompletionPolicyChanged }
  | { type: 'task_event'; event: TrellusTaskEvent };

export interface TrellusPublishSelectionInput {
  /** The WHOLE checked set (not a delta), unique, <= 10,000. `[]` explicitly clears. Omit when only focus changed. */
  selected_ids?: readonly string[];
  /** Exact record the user opened. `null` clears the advisory. Omit when only checked rows changed. */
  focused_id?: string | null;
  /** When given, the broadcast is skipped unless the extension's current view is this table. */
  kind?: TrellusSelectionKind;
  /**
   * The scoped list this broadcast belongs to (an account roster). When the extension's ACTIVE context is not that list
   * (e.g. `kind:null` before any selection), the client looks the list up in `observed_sources` and publishes against
   * that entry's exact `source_url` instead of giving up with `view_unavailable`.
   */
  scope?: TrellusDialScope;
  /**
   * The exact `source_url` the availability latch remembers for `scope`. Used only when the FRESH context no longer lists
   * the source (a later reply may drop `observed_sources`): the client still asks the extension for that exact URL and
   * publishes only against its own answer. Ignored unless it carries `scope`'s account.
   */
  latchedSourceUrl?: string;
  /** Aborts the publish: a not-yet-sent selection is dropped, an awaiting one settles `superseded`. */
  signal?: AbortSignal;
}

export interface TrellusHostClientOptions {
  target?: TrellusEventTarget;
  timeoutMs?: number;
  /** Latches the document into the extension's test barrier (no real calls / Gong writes). QA only. */
  isTest?: boolean;
}

/** The slice of `Document` the client needs — lets tests pass a fake. */
export interface TrellusEventTarget {
  addEventListener: (type: string, listener: (event: Event) => void) => void;
  removeEventListener: (type: string, listener: (event: Event) => void) => void;
  dispatchEvent: (event: Event) => boolean;
  defaultView?: { CustomEvent?: typeof CustomEvent } | null;
}

export interface TrellusHostClient {
  /** Capability handshake. Cached until the nonce changes or a command reports `stale_context`. */
  connect: () => Promise<TrellusResult<TrellusCapabilities>>;
  /** Latest capabilities (null before connect / after the document identity changed). */
  getCapabilities: () => TrellusCapabilities | null;
  /** Fresh `HOST_CONTEXT` handshake. `sourceUrl` asks the extension for the context of that observed list (sent verbatim). */
  getContext: (options?: { sourceUrl?: string }) => Promise<TrellusResult<TrellusContext>>;
  /** Context handshake, then one `HOST_SELECTION` for the current (nonce, revision). */
  publishSelection: (input: TrellusPublishSelectionInput) => Promise<TrellusResult<TrellusSelectionResult>>;
  /** v2 `HostCommand` envelope with `request_id` correlation. Resolves with the terminal `payload`. */
  sendCommand: (command: string, payload?: Record<string, unknown>) => Promise<TrellusResult<unknown>>;
  replayEvents: (afterEventSequence?: number) => Promise<TrellusResult<unknown>>;
  subscribe: (listener: (event: TrellusClientEvent) => void) => () => void;
  /** Removes every listener and timer; pending calls resolve `{ ok:false, reason:'disposed' }`. Idempotent. */
  dispose: () => void;
}

/** The extension context a publish goes against: one list at one revision of one document. */
type PublishContext = { nonce: string; revision: number; kind: TrellusSelectionKind; source_url: string };

/** The ACTIVE context as a publish target, or null when it names no usable list. */
function activeSource(active: TrellusContext): PublishContext | null {
  if (!active.available || active.kind === null || active.source_url === null) return null;
  return { nonce: active.nonce, revision: active.revision, kind: active.kind, source_url: active.source_url };
}

/** The latched URL as a lookup entry, only when it carries `scope`'s account (a stale or foreign latch is ignored). */
function latchedEntry(scope: TrellusDialScope, latchedSourceUrl: string | undefined): TrellusScopedSource | null {
  if (latchedSourceUrl === undefined) return null;
  const entry: TrellusScopedSource = { kind: 'contacts', source_url: latchedSourceUrl, available: true };
  return sourceMatchesScope(entry, scope) ? entry : null;
}

/**
 * Is this in-flight selection obsolete under a newly ingested context? A new nonce (another document) obsoletes every
 * selection. Otherwise a context naming a list (`source_url` set) obsoletes selections for any other (list, revision):
 * the extension clears its host selection and resets the sequence on that change, so a late acknowledgement for the old
 * one must not read as success. A context with no list (`kind:null`, pushed on any checkbox change) says nothing about a
 * scoped publish in flight and obsoletes nothing.
 */
function obsoletedBy(context: TrellusContext, owner: PublishContext, solicited: boolean): boolean {
  if (owner.nonce !== context.nonce) return true;
  // A reply to a context request WE made is a requested snapshot (possibly a delayed one for a superseded publish), not a
  // push saying the active list moved: it never obsoletes a selection by list or revision, only a document reload does.
  if (solicited) return false;
  return context.source_url !== null && (owner.source_url !== context.source_url || owner.revision !== context.revision);
}

type Pending =
  | { kind: 'capabilities'; settle: (r: TrellusResult<TrellusCapabilities>) => void; timer: ReturnType<typeof setTimeout> }
  | { kind: 'context'; settle: (r: TrellusResult<TrellusContext>) => void; timer: ReturnType<typeof setTimeout> }
  | {
    kind: 'selection';
    sequence: number;
    /** The `nonce|revision|source_url` the sequence counter was owned by when this was sent. */
    sequenceKey: string;
    /** The (nonce, revision, list) this selection was sent against. */
    owner: PublishContext;
    settle: (r: TrellusResult<TrellusSelectionResult>) => void;
    timer: ReturnType<typeof setTimeout>;
  }
  | { kind: 'command'; command: string; settle: (r: TrellusResult<unknown>) => void; timer: ReturnType<typeof setTimeout> };

function newRequestId(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

type TrellusFailure = Extract<TrellusResult<never>, { ok: false }>;

const failure = (reason: TrellusFailureReason, extra: { reason_code?: string | null; retryable?: boolean } = {}): TrellusFailure => ({ ok: false, reason, ...extra });

function validateSelectedIds(ids: readonly string[]): boolean {
  if (ids.length > TRELLUS_MAX_SELECTED_IDS) return false;
  const seen = new Set<string>();
  for (const id of ids) {
    if (typeof id !== 'string' || id.length === 0 || seen.has(id)) return false;
    seen.add(id);
  }
  return true;
}

export function createTrellusHostClient(options: TrellusHostClientOptions = {}): TrellusHostClient {
  const target: TrellusEventTarget = options.target ?? document;
  const timeoutMs = options.timeoutMs ?? TRELLUS_DEFAULT_TIMEOUT_MS;
  const isTest = options.isTest === true;
  const EventClass = target.defaultView?.CustomEvent ?? globalThis.CustomEvent;

  const pending = new Map<string, Pending>();
  const subscribers = new Set<(event: TrellusClientEvent) => void>();
  const receivedEventIds = new Set<string>();

  let disposed = false;
  let capabilities: TrellusCapabilities | null = null;
  let connecting: Promise<TrellusResult<TrellusCapabilities>> | null = null;
  // Sequence counter, owned per (nonce, revision, source_url): reset whenever that tuple changes (at publish time).
  let sequenceKey: string | null = null;
  let sequence = 0;
  // The extension's document identity as last seen on a context; a change means it reloaded.
  let knownNonce: string | null = null;
  // Latest-wins gate for publishSelection: a newer call makes an older one stand down.
  let publishGeneration = 0;

  const sequenceKeyOf = (c: { nonce: string; revision: number; source_url: string }): string => `${c.nonce}|${c.revision}|${c.source_url}`;

  const emit = (event: TrellusClientEvent): void => {
    for (const fn of subscribers) {
      try { fn(event); } catch (err) { log.debug({ err }, 'trellus subscriber threw'); }
    }
  };

  /** Remove a pending request (and its timer) so exactly one outcome settles it. */
  const take = (id: string): Pending | undefined => {
    const entry = pending.get(id);
    if (!entry) return undefined;
    pending.delete(id);
    clearTimeout(entry.timer);
    return entry;
  };

  /** A failure is assignable to every request's result type, so one helper settles any kind. */
  const fail = (id: string, result: TrellusFailure): void => {
    take(id)?.settle(result);
  };

  const setCapabilities = (next: TrellusCapabilities | null): void => {
    capabilities = next;
    emit({ type: 'capabilities', capabilities: next });
  };

  const ingestContext = (context: TrellusContext, solicited: boolean): void => {
    if (knownNonce !== null && knownNonce !== context.nonce) {
      // A new document identity: the sequence restarts (everything in flight was for the old one, see `obsoletedBy`).
      sequenceKey = null;
      sequence = 0;
    }
    for (const [id, entry] of pending) {
      if (entry.kind === 'selection' && obsoletedBy(context, entry.owner, solicited)) fail(id, failure('context_changed'));
    }
    knownNonce = context.nonce;
    if (capabilities && capabilities.nonce !== context.nonce) {
      // The document identity changed (extension reloaded): capabilities are stale.
      receivedEventIds.clear();
      setCapabilities(null);
    }
    emit({ type: 'context', context });
  };

  /** Dispatch a JSON `detail` on `target`; false when dispatch itself threw. */
  const dispatch = (name: string, body: Record<string, unknown>): boolean => {
    try {
      target.dispatchEvent(new EventClass(name, { detail: JSON.stringify(body) }));
      return true;
    } catch (err) {
      log.debug({ err, event: name }, 'trellus dispatch failed');
      return false;
    }
  };

  /** Register BEFORE dispatching: a receiver may reply synchronously. */
  const request = <T>(
    id: string,
    build: (settleFn: (r: TrellusResult<T>) => void, timer: ReturnType<typeof setTimeout>) => Pending,
    eventName: string,
    body: Record<string, unknown>,
  ): Promise<TrellusResult<T>> => new Promise(resolve => {
    const timer = setTimeout(() => fail(id, failure('timeout')), timeoutMs);
    pending.set(id, build(resolve, timer));
    if (!dispatch(eventName, body)) fail(id, failure('dispatch_failed'));
  });

  const parseDetail = <S extends z.ZodType>(eventName: string, event: Event, schema: S, maxChars: number): z.infer<S> | null => {
    const detail: unknown = 'detail' in event ? event.detail : undefined;
    if (typeof detail !== 'string' || detail.length > maxChars) {
      log.debug({ event: eventName }, 'trellus event ignored: detail missing or oversized');
      return null;
    }
    const parsed = safeParseJson(schema, detail);
    if (!parsed.success) {
      log.debug({ event: eventName, reason: parsed.error.message }, 'trellus event ignored: malformed');
      return null;
    }
    return parsed.data;
  };

  const handlers: Array<[string, (event: Event) => void]> = [];
  const on = <S extends z.ZodType>(name: string, schema: S, handle: (data: z.infer<S>) => void, maxChars = TRELLUS_MAX_DETAIL_CHARS): void => {
    const listener = (event: Event): void => {
      if (disposed) return;
      const data = parseDetail(name, event, schema, maxChars);
      if (data === null) return;
      try { handle(data); } catch (err) { log.debug({ err, event: name }, 'trellus handler threw'); }
    };
    handlers.push([name, listener]);
    target.addEventListener(name, listener);
  };

  on(TRELLUS_EVENTS.capabilities, TRELLUS_INBOUND_SCHEMAS.capabilities, response => {
    const entry = pending.get(response.request_id);
    if (!entry || entry.kind !== 'capabilities') return;
    if (!response.available) {
      fail(response.request_id, failure('unavailable', { reason_code: response.reason_code ?? 'extension_unavailable' }));
      return;
    }
    if (capabilities && capabilities.nonce !== response.nonce) receivedEventIds.clear();
    capabilities = response;
    emit({ type: 'capabilities', capabilities: response });
    take(response.request_id);
    entry.settle({ ok: true, value: response });
  });

  on(TRELLUS_EVENTS.context, TRELLUS_INBOUND_SCHEMAS.context, context => {
    const entry = context.request_id ? pending.get(context.request_id) : undefined;
    ingestContext(context, entry?.kind === 'context');
    if (context.request_id && entry?.kind === 'context') {
      take(context.request_id);
      entry.settle({ ok: true, value: context });
    }
  }, TRELLUS_MAX_ID_LIST_DETAIL_CHARS);

  on(TRELLUS_EVENTS.selectionResult, TRELLUS_INBOUND_SCHEMAS.selectionResult, result => {
    const entry = pending.get(result.request_id);
    // Only replies to OUR requests count; stray results are not ours to interpret.
    if (!entry || entry.kind !== 'selection') return;
    if (entry.sequenceKey !== sequenceKey || entry.sequence < sequence) { fail(result.request_id, failure('superseded')); return; }
    if ((result.nonce !== undefined && result.nonce !== entry.owner.nonce)
      || (result.revision !== undefined && result.revision !== entry.owner.revision)
      || (typeof result.source_url === 'string' && result.source_url !== entry.owner.source_url)) {
      fail(result.request_id, failure('context_changed'));
      return;
    }
    emit({ type: 'selection_result', result });
    if (result.accepted) {
      take(result.request_id);
      entry.settle({ ok: true, value: result });
      return;
    }
    fail(result.request_id, failure('rejected', { reason_code: result.reason_code ?? null }));
  }, TRELLUS_MAX_ID_LIST_DETAIL_CHARS);

  on(TRELLUS_EVENTS.focusResult, TRELLUS_INBOUND_SCHEMAS.focusResult, result => {
    emit({ type: 'focus_result', result });
  });

  on(TRELLUS_EVENTS.commandResult, TRELLUS_INBOUND_SCHEMAS.commandResult, response => {
    const entry = pending.get(response.request_id);
    if (!entry || entry.kind !== 'command' || entry.command !== response.command) return;
    if (response.status === 'pending') return;
    if (response.status === 'rejected') {
      if (response.reason_code === 'stale_context' || response.reason_code === 'extension_disabled') setCapabilities(null);
      fail(response.request_id, failure('rejected', { reason_code: response.reason_code ?? 'command_rejected', retryable: response.retryable === true }));
      return;
    }
    take(response.request_id);
    entry.settle({ ok: true, value: response.payload ?? {} });
  });

  on(TRELLUS_EVENTS.sidebarState, TRELLUS_INBOUND_SCHEMAS.sidebarState, state => emit({ type: 'sidebar_state', state }));
  on(TRELLUS_EVENTS.bulkDialResult, TRELLUS_INBOUND_SCHEMAS.bulkDialResult, result => emit({ type: 'bulk_dial_result', result }));
  on(TRELLUS_EVENTS.gongConnectionState, TRELLUS_INBOUND_SCHEMAS.gongConnectionState, state => emit({ type: 'gong_connection_state', state }));
  on(TRELLUS_EVENTS.uiConfigurationChanged, TRELLUS_INBOUND_SCHEMAS.uiConfigurationChanged, change => emit({ type: 'ui_configuration_changed', change }));
  on(TRELLUS_EVENTS.completionPolicyChanged, TRELLUS_INBOUND_SCHEMAS.completionPolicyChanged, change => emit({ type: 'completion_policy_changed', change }));

  on(TRELLUS_EVENTS.taskEvent, TRELLUS_INBOUND_SCHEMAS.taskEvent, event => {
    // Same gate as the reference client: only advertised types, only this document's nonce, once per event_id.
    if (!capabilities || !capabilities.outbound_events.includes(event.event_type) || event.nonce !== capabilities.nonce) {
      log.debug({ event_type: event.event_type }, 'trellus task event ignored: not advertised / wrong nonce');
      return;
    }
    if (receivedEventIds.has(event.event_id)) return;
    receivedEventIds.add(event.event_id);
    if (receivedEventIds.size > RECEIVED_EVENT_ID_CAP) {
      const oldest = receivedEventIds.values().next().value;
      if (oldest !== undefined) receivedEventIds.delete(oldest);
    }
    emit({ type: 'task_event', event });
  });

  const connect = (): Promise<TrellusResult<TrellusCapabilities>> => {
    if (disposed) return Promise.resolve(failure('disposed'));
    if (capabilities) return Promise.resolve({ ok: true, value: capabilities });
    if (!connecting) {
      const id = newRequestId();
      connecting = request<TrellusCapabilities>(
        id,
        (settleFn, timer) => ({ kind: 'capabilities', settle: settleFn, timer }),
        TRELLUS_EVENTS.capabilitiesRequest,
        { version: 2, request_id: id, ...(isTest ? { is_test: true } : {}) },
      ).finally(() => { connecting = null; });
    }
    return connecting;
  };

  const getContext = (query: { sourceUrl?: string } = {}): Promise<TrellusResult<TrellusContext>> => {
    if (disposed) return Promise.resolve(failure('disposed'));
    const id = newRequestId();
    return request<TrellusContext>(
      id,
      (settleFn, timer) => ({ kind: 'context', settle: settleFn, timer }),
      TRELLUS_EVENTS.contextRequest,
      { version: 1, request_id: id, ...(query.sourceUrl !== undefined ? { source_url: query.sourceUrl } : {}) },
    );
  };

  /**
   * The context a scoped publish goes against: ONLY the extension's own answer for that exact observed list. A refused or
   * mismatched reply is a typed failure and nothing is published (the extension never silently selects another table, and
   * Terra never invents a nonce/revision for one). The entry may come from the latch (`latchedSourceUrl`): it is only the
   * URL to ASK for; the nonce/revision always come from the extension's reply.
   */
  const resolveScopedContext = async (
    active: TrellusContext,
    scope: TrellusDialScope,
    latchedSourceUrl: string | undefined,
    stale: () => boolean,
  ): Promise<TrellusResult<PublishContext>> => {
    // A fresh observation wins; the latched URL only stands in when the fresh context no longer lists the source.
    const entry = findObservedScopedSource(active, scope) ?? latchedEntry(scope, latchedSourceUrl);
    if (!entry) return failure('view_unavailable', { reason_code: 'scoped_source_not_observed' });
    const scoped = await getContext({ sourceUrl: entry.source_url });
    if (stale()) return failure(disposed ? 'disposed' : 'superseded');
    if (!scoped.ok) return scoped;
    const reply = scoped.value;
    if (!reply.available || reply.kind !== 'contacts' || reply.source_url !== entry.source_url) {
      return failure('view_unavailable', { reason_code: reply.reason_code ?? 'scoped_source_unavailable', ...(reply.retryable !== undefined ? { retryable: reply.retryable } : {}) });
    }
    return { ok: true, value: { nonce: reply.nonce, revision: reply.revision, kind: 'contacts', source_url: entry.source_url } };
  };

  const publishSelection = async (input: TrellusPublishSelectionInput): Promise<TrellusResult<TrellusSelectionResult>> => {
    if (disposed) return failure('disposed');
    const hasSelected = input.selected_ids !== undefined;
    const hasFocus = input.focused_id !== undefined;
    if ((!hasSelected && !hasFocus) || (input.selected_ids && !validateSelectedIds(input.selected_ids))
      || (typeof input.focused_id === 'string' && input.focused_id.length === 0)) {
      return failure('invalid_request');
    }
    const { scope, signal } = input;
    const generation = ++publishGeneration;
    /** True once this call must not send: disposed, a newer publish took over, or the caller aborted. */
    const stale = (): boolean => disposed || generation !== publishGeneration || signal?.aborted === true;
    const staleFailure = (): TrellusFailure => failure(disposed ? 'disposed' : 'superseded');
    if (signal?.aborted) return staleFailure();
    // Fresh handshake before EVERY user-driven broadcast.
    const handshake = await getContext();
    if (stale()) return staleFailure();
    if (!handshake.ok) return handshake;
    const active = handshake.value;

    let context: PublishContext;
    const activeCtx = activeSource(active);
    if (activeCtx !== null && (!scope || sourceMatchesScope(activeCtx, scope))) {
      context = activeCtx;
    } else if (scope) {
      const scoped = await resolveScopedContext(active, scope, input.latchedSourceUrl, stale);
      if (!scoped.ok) return scoped;
      context = scoped.value;
    } else {
      return failure('view_unavailable');
    }
    if (input.kind && context.kind !== input.kind) return failure('kind_mismatch');

    // The scoped await above is a cancellation window: a caller abort, a newer publish or a dispose may have landed in it.
    if (stale()) return staleFailure();
    const key = sequenceKeyOf(context);
    if (key !== sequenceKey) {
      sequenceKey = key;
      sequence = 0;
    }
    const id = newRequestId();
    const seq = ++sequence;
    const result = request<TrellusSelectionResult>(
      id,
      (settleFn, timer) => ({ kind: 'selection', sequence: seq, sequenceKey: key, owner: context, settle: settleFn, timer }),
      TRELLUS_EVENTS.selection,
      {
        version: 1,
        request_id: id,
        nonce: context.nonce,
        revision: context.revision,
        sequence: seq,
        kind: context.kind,
        source_url: context.source_url,
        ...(hasSelected ? { selected_ids: [...(input.selected_ids ?? [])] } : {}),
        ...(hasFocus ? { focused_id: input.focused_id } : {}),
      },
    );
    if (!signal) return result;
    // Aborted while waiting for the acknowledgement: stop waiting (the caller must not record this set as published).
    const onAbort = (): void => fail(id, failure('superseded'));
    signal.addEventListener('abort', onAbort, { once: true });
    // An abort that fired synchronously during the dispatch above was missed by the listener.
    if (signal.aborted) onAbort();
    return result.finally(() => signal.removeEventListener('abort', onAbort));
  };

  const sendCommand = async (command: string, payload: Record<string, unknown> = {}): Promise<TrellusResult<unknown>> => {
    if (disposed) return failure('disposed');
    const handshake = capabilities ? { ok: true as const, value: capabilities } : await connect();
    if (disposed) return failure('disposed');
    if (!handshake.ok) return handshake;
    const caps = handshake.value;
    if (!caps.commands.includes(command)) return failure('unsupported');

    const id = newRequestId();
    const ttl = Math.min(COMMAND_EXPIRY_MS, timeoutMs, caps.limits?.command_timeout_ms ?? COMMAND_EXPIRY_MS);
    const body = {
      version: 2,
      request_id: id,
      nonce: caps.nonce,
      expires_at_ms: Date.now() + ttl,
      command,
      ...(isTest ? { is_test: true } : {}),
      payload,
    };
    if (JSON.stringify(body).length > MAX_COMMAND_BYTES) return failure('invalid_request');
    return request<unknown>(
      id,
      (settleFn, timer) => ({ kind: 'command', command, settle: settleFn, timer }),
      TRELLUS_EVENTS.command,
      body,
    );
  };

  return {
    connect,
    getCapabilities: () => capabilities,
    getContext,
    publishSelection,
    sendCommand,
    replayEvents: (afterEventSequence = 0) => sendCommand('events.replay', { after_event_sequence: afterEventSequence }),
    subscribe(listener) {
      subscribers.add(listener);
      return () => { subscribers.delete(listener); };
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const [name, listener] of handlers) target.removeEventListener(name, listener);
      handlers.length = 0;
      for (const id of pending.keys()) fail(id, failure('disposed'));
      subscribers.clear();
      receivedEventIds.clear();
      capabilities = null;
      connecting = null;
    },
  };
}
